/**
 * ZoomToGeo container (refactored implementation).
 * Reactive-only: responds to URLParameterController messages (from: "urlParams")
 * and user dropdown interactions, without changing existing behavior.
 */

import Typography from "@mui/material/Typography";
import React, { useCallback, useEffect, useState } from "react";
import APIHelper from "@giscon/utils.api-helper-esri";
import LayerStore from "@giscon/utils.layer-store";
import { observer } from "mobx-react";

import HelpLink from "../HelpLink";
import { REGIONAL_SELECTION_DEFAULTS } from "configs/config";

import RegionDropdowns from "./RegionDropdowns";
import SelectionActions from "./SelectionActions";
import ErrorsPanel from "./ErrorsPanel";
import TooSmallWarning from "./TooSmallWarning";

import { defaultNls, messageIds, selectionFrom } from "./constants";

import useGraphicsLayer from "./hooks/useGraphicsLayer";
import useRegionalSelectionServices from "./hooks/useRegionalSelectionServices";
import useServiceItemOptions from "./hooks/useServiceItemOptions";
import useServiceItemGraphic from "./hooks/useServiceItemGraphic";
import useSelectionGraphicMessaging from "./hooks/useSelectionGraphicMessaging";
import useApplyUrlServiceAndItem from "./hooks/useApplyUrlServiceAndItem";

function ZoomToGeo({
  allowedEntityNames = REGIONAL_SELECTION_DEFAULTS.allowedEntities,
  nls = defaultNls,
  CommunicationStore,
  onChangeSelection,
  // graphicTooLittle,
}) {
  const { map, view } = LayerStore;
  const graphicsLayer = useGraphicsLayer({ map, view, keepOnTop: true });

  const { services, loadingServices, servicesError } =
    useRegionalSelectionServices({ CommunicationStore });

  const storeSelection = CommunicationStore?.getMessagesToID?.({
    to: messageIds.selectionGraphic,
  })?.message;

  const selection = storeSelection?.graphic;
  const from = storeSelection?.from;

  const [service, setService] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const { serviceItems, loadingServiceItems, serviceItemsError } =
    useServiceItemOptions({ service, allowedEntityNames });

  const { graphic, setGraphic, geometryError } = useServiceItemGraphic({
    service,
    selectedItem,
  });

  /**
   * Zooms the map view to the current graphic's geometry.
   * Waits for the view to be ready, then fits the geometry after a layout tick
   * (i.e., after browser layout/rendering is complete, using requestAnimationFrame).
   * Ignores errors from interrupted or overlapping goTo calls.
   */
  const zoomToGraphic = useCallback(() => {
    const geometry = graphic?.geometry;
    if (!geometry || !view) return;
    if (typeof view.when !== "function" || typeof view.goTo !== "function") {
      return;
    }

    view.when().then(() => {
      window.requestAnimationFrame(() => {
        view.goTo({ target: geometry }, { animate: false }).catch(() => {});
      });
    });
  }, [graphic, view]);

  useEffect(() => {
    if (!graphicsLayer?.layer) return;

    APIHelper.clearLayer(graphicsLayer.layer);
    if (graphic) {
      APIHelper.addGraphicToLayer(graphic, graphicsLayer.layer);
      zoomToGraphic();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphic, graphicsLayer?.layer]);

  // Reset dependent state on service change (matches legacy behavior)
  useEffect(() => {
    setSelectedItem(null);
    setGraphic(null);
  }, [service, setGraphic]);

  useApplyUrlServiceAndItem({
    storeSelection,
    services,
    selectedService: service,
    setSelectedService: setService,
    serviceItems,
    selectedItem,
    setSelectedItem,
  });

  useSelectionGraphicMessaging({
    CommunicationStore,
    graphic,
    selectedEntity: selectedItem,
    source: service,
    previousFrom: from,
  });

  // selection was set by another widget -> clear local selection
  // (except when from urlParams or this widget)
  useEffect(() => {
    if (
      storeSelection?.from &&
      storeSelection.from !== selectionFrom.urlParams &&
      storeSelection.from !== selectionFrom.zoomToGeo &&
      storeSelection?.graphic !== graphic
    ) {
      setGraphic(null);
      setSelectedItem(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeSelection, graphic]);

  const clearSelection = () => {
    setGraphic(null);
    setSelectedItem(null);
    setService(null);

    CommunicationStore?.addMessage?.({
      to: messageIds.selectionGraphic,
      message: {
        graphic: null,
        entity: null,
        source: null,
        from: selectionFrom.zoomToGeo,
      },
    });
  };

  return (
    <div
      style={{
        display: "block",
        border: "solid 1px lightgray",
        borderRadius: 5,
        padding: 10,
      }}
    >
      <Typography data-cy='zoom-to-geo-title'>{nls.title}</Typography>
      <HelpLink
        className="help-link"
        size="20px"
        url="/hauptfunktionen/werkzeugleiste/raeumliche-auswahl"
      />

      <ErrorsPanel
        servicesError={servicesError}
        servicesErrorText={nls.errorLoadingServices}
        itemsError={serviceItemsError}
        itemsErrorText={nls.errorLoadingItems}
        geometryError={geometryError}
        geometryErrorText={nls.errorLoadingGeometry}
      />

      <RegionDropdowns
        map={map}
        view={view}
        services={services}
        loadingServices={loadingServices}
        selectedService={service}
        onSelectService={(e, value) => {
          setService(value);
          onChangeSelection?.();
        }}
        items={serviceItems}
        loadingItems={loadingServiceItems}
        selectedItem={selectedItem}
        onSelectItem={(e, value) => {
          setSelectedItem(value);
          onChangeSelection?.();
        }}
        nls={nls}
      />

      <SelectionActions
        showZoom={Boolean(graphic)}
        zoomLabel={nls.zoomToSelectedLayer}
        onZoom={zoomToGraphic}
        showClear={Boolean(selection)}
        clearLabel={nls.clear}
        onClear={() => {
          clearSelection();
          onChangeSelection?.();
        }}
      />

      <TooSmallWarning
        // visible={Boolean(graphicTooLittle)}
        visible={Boolean(
          selectedItem &&
            "hasProjectionData" in selectedItem &&
            !selectedItem.hasProjectionData
        )}
        text={nls.tooLittle}
      />
    </div>
  );
}

export default observer(ZoomToGeo);
